/**
 * Autocomplete mode for the LocationPicker module.
 *
 * Clean rewrite of the original compare autocomplete-input logic (previously
 * ~346 lines of imperative jQuery). Uses jQuery UI autocomplete (existing
 * dependency) with state reads from LocationSlot/LocationStore instead of
 * DOM scraping. Syncs DOM from slot state and delegates filtering to
 * LocationSlot#filterLocations.
 */
import '../../jquery-ui';
import { initLocale } from '../../i18n';
import { MODES } from './state';

const MAIN_SEL = role => `.component-search-${role}`;
const TRUNCATE_ADDRESS_LEN = 50;

const normalizeAr = s => (s || '').replace(/[أإ]/g, 'ا').toLowerCase().trim();

function truncateAddress(str) {
  if (!str || typeof str !== 'string') return '';
  const s = str.trim();
  return s.length <= TRUNCATE_ADDRESS_LEN ? s : s.slice(0, TRUNCATE_ADDRESS_LEN) + '…';
}

/**
 * Search locations against a typed term. Returns results grouped by
 * exact match → city/name match → country/code match, capped at 25.
 */
function searchLocations(source, term) {
  const lower = term.toLowerCase();
  const arTerm = normalizeAr(term);
  const exact = [];
  const cities = [];
  const countries = [];

  for (const item of source) {
    const valLower = item.value.toLowerCase().trim();
    const valAr = item.value_ar ? normalizeAr(item.value_ar) : null;

    if ((valAr && valAr === arTerm) || valLower === lower) {
      exact.push(item);
    } else if ((valAr && valAr.includes(arTerm)) || item.value.toLowerCase().includes(lower)) {
      cities.push(item);
    } else if (
      (item.country_ar && normalizeAr(item.country_ar).includes(arTerm))
      || item.country.toLowerCase().includes(lower)
      || (item.code && item.code.toLowerCase().includes(lower))
    ) {
      countries.push(item);
    }
  }

  return exact.concat(cities, countries).slice(0, 25);
}

function renderDropdownItem(item) {
  return `
    <li class='notranslate' translate='no' data-port="${item.port}">
      <div class="search-select-tag">${item.port ? 'Ports' : 'Areas'}</div>
      <div class="search-select-item">
        <span class="search-select-item-img">
          <img src="${item.imgUrl}" alt="${item.display_country}"/>
        </span>
        <span class="search-select-item-icon">
          <img src="${item.icon}" alt="${item.port ? 'port' : 'area'}"/>
        </span>
        <span class="search-select-item-label">
          <span class="truncate">${item.display_name}</span>
          <span class="truncate">${item.display_country}</span>
        </span>
      </div>
    </li>`;
}

/**
 * Manages the autocomplete UI for a single {@link LocationSlot}. Binds
 * jQuery UI autocomplete to the location and extra inputs, keeps the DOM
 * in sync with slot state, and delegates filtering to {@link LocationSlot#filterLocations}.
 */
class SlotAutocomplete {
  constructor(slot, store, translation) {
    this._slot = slot;
    this._store = store;
    this._t = translation;
    this._main = MAIN_SEL(slot.role);
    this._handlers = [];
    this._changeUnsub = null;
    this._pairedUnsub = null;
  }

  attach() {
    const { _slot: slot, _main: main } = this;

    this._initAutocomplete(`${main} .location-search`, 'location');
    this._initAutocomplete(`${main} .extra-search`, 'extra');
    this._bindDOMEvents();

    this._changeUnsub = () => this._syncDOM();
    slot.on('change', this._changeUnsub);

    if (slot.paired) {
      this._pairedUnsub = () => this._syncDOM();
      slot.paired.on('change', this._pairedUnsub);
    }

    this._hydrateField('location', slot.initialData.location);
    this._hydrateField('extra', slot.initialData.extra);

    this._syncDOM();
  }

  detach() {
    const { _slot: slot, _main: main } = this;

    if (this._changeUnsub) {
      slot.off('change', this._changeUnsub);
      this._changeUnsub = null;
    }
    if (this._pairedUnsub && slot.paired) {
      slot.paired.off('change', this._pairedUnsub);
      this._pairedUnsub = null;
    }

    for (const { $el, event, handler } of this._handlers) {
      $el.off(event, handler);
    }
    this._handlers = [];

    for (const sel of [`${main} .location-search`, `${main} .extra-search`]) {
      const $input = $(sel);
      if ($input.data('uiAutocomplete') || $input.data('ui-autocomplete')) {
        $input.autocomplete('destroy');
      }
    }
  }

  // ── jQuery UI autocomplete ─────────────────────────────

  _initAutocomplete(inputSel, target) {
    const $input = $(inputSel);
    if (!$input.length) return;

    const slot = this._slot;
    const store = this._store;

    $input.autocomplete({
      classes: { 'ui-autocomplete': 'autocomplete-country-select-list' },
      minLength: 2,
      delay: 500,
      source: (request, response) => {
        const pool = slot.filterLocations(store.locations, target);
        response(searchLocations(pool, request.term));
      },
      select: (_e, ui) => {
        this._selectItem(ui.item, target);
        return false;
      },
      close: () => this._validateField(target),
      change: () => this._validateField(target),
      open: function () {
        const { x: left } = $(this)
          .closest('.component-search__input-wrapper')
          .get(0)
          .getBoundingClientRect();
        $('.autocomplete-country-select-list:visible').css({ top: '+=18', left });
      },
    });

    $input.on('focus', function () {
      $(this).autocomplete('search');
    });

    $input.autocomplete('instance')._renderItem = function (ul, item) {
      return $(renderDropdownItem(item)).appendTo(ul);
    };
  }

  _selectItem(item, target) {
    if (target === 'extra') {
      this._slot.setExtra(item);
    } else {
      this._slot.setLocation(item);
    }
  }

  // ── Hydration ──────────────────────────────────────────

  _hydrateField(target, rawValue) {
    if (!rawValue) return;

    const { _slot: slot, _store: store } = this;
    let resolved = null;

    const numId = Number(rawValue);
    if (Number.isInteger(numId) && numId > 0) {
      resolved = store.locations.find(l => l.id === numId) || null;
    }

    if (!resolved) {
      const pool = slot.filterLocations(store.locations, target);
      const results = searchLocations(pool, String(rawValue));
      if (results.length) resolved = results[0];
    }

    if (!resolved) return;

    if (target === 'extra') {
      slot.setExtra(resolved);
      this._setMultiInput(true);
    } else {
      slot.setLocation(resolved);
    }
  }

  // ── DOM events ─────────────────────────────────────────

  _bindDOMEvents() {
    const { _slot: slot, _main: main } = this;

    this._on(`${main} .optional-area`, 'click', () => {
      $(`${main} .optional-area`).css('display', 'none');
      this._setMultiInput(true);
      slot.demoteLocation();
    });

    this._on(`${main} .optional-port`, 'click', () => {
      $(`${main} .optional-port`).css('display', 'none');
      this._setMultiInput(true);
    });

    this._on(
      `${main} .location-selector .component-search__close-button`,
      'click',
      () => {
        if (slot.isMapPick) {
          slot.clearMapPick();
        } else {
          slot.promoteExtra();
          this._setMultiInput(false);
        }
      },
    );

    this._on(
      `${main} .extra-selector .component-search__close-button`,
      'click',
      () => {
        this._setMultiInput(false);
        slot.clearExtra();
      },
    );

    this._on(`${main} .location-selector input[type="search"]`, 'input', e => {
      if ($(e.currentTarget).val() === '') {
        if (slot.isMapPick) slot.clearMapPick();
        else slot.promoteExtra();
        this._setMultiInput(false);
      }
    });

    this._on(`${main} .extra-selector input[type="search"]`, 'input', e => {
      if ($(e.currentTarget).val() === '') {
        this._setMultiInput(false);
        slot.clearExtra();
      }
    });

    this._on(`${main} .flag-wrapper`, 'click', function () {
      $(this).siblings('input').trigger('focus');
    });
  }

  _on(selector, event, handler) {
    const $el = $(selector);
    $el.off(event).on(event, handler);
    this._handlers.push({ $el, event, handler });
  }

  // ── Multi-input toggle ─────────────────────────────────

  _setMultiInput(show) {
    const main = this._main;
    $(`${main} .extra-selector`).css('display', show ? 'flex' : 'none');

    const $flex = $(`${main} .flexible-input`);
    if (show) {
      $flex.addClass('merged-input multi-input');
    } else {
      $flex.removeClass('merged-input multi-input');
    }

    $(`${main} .extra-selector .component-search__close-button`).css('display', show ? 'block' : 'none');
  }

  // ── DOM sync ───────────────────────────────────────────

  _syncDOM() {
    const { _slot: slot, _main: main, _store: store } = this;

    this._syncLabels();

    const prefix = slot.isOrigin ? 'origin' : 'destination';

    if (slot.isMapPick) {
      const pick = slot.mapPick;
      const $loc = $(`${main} .location-selector`);
      $loc.find('input[type="search"]').val(truncateAddress(pick.displayAddress)).prop('disabled', true);
      $loc.find('input#origin-location-hidden, input#destination-location-hidden').attr('name', '').val('');
      $loc.find('.origin-location-name-hidden, .destination-location-name-hidden').attr('name', '').val('');
      $loc.find(`input[name="${prefix}_lat"]`).val(String(pick.lat));
      $loc.find(`input[name="${prefix}_lng"]`).val(String(pick.lng));
      $loc.find('.flag-wrapper').css('display', 'none');
      $loc.find('.marker-wrapper').css('display', 'inline-block');
    } else {
      const $loc = $(`${main} .location-selector`);
      $loc.find('input[type="search"]').prop('disabled', slot.location !== null);
      $loc.find(`input[name="${prefix}_lat"]`).val('');
      $loc.find(`input[name="${prefix}_lng"]`).val('');
      $(`${main} .location-selector input#origin-location-hidden, ${main} .location-selector input#destination-location-hidden`).attr('name', slot.locationFieldName());
      $(`${main} .location-selector .origin-location-name-hidden, ${main} .location-selector .destination-location-name-hidden`).attr('name', slot.locationFieldNameForSeo());
      this._syncFieldDOM(`${main} .location-selector`, slot.location);
    }

    $(`${main} .extra-selector input#origin-extra-hidden, ${main} .extra-selector input#destination-extra-hidden`).attr('name', slot.extraFieldName());
    $(`${main} .extra-selector .origin-extra-name-hidden, ${main} .extra-selector .destination-extra-name-hidden`).attr('name', slot.extraFieldNameForSeo());
    this._syncFieldDOM(`${main} .extra-selector`, slot.extra);
    $(`${main} .extra-selector input[type="search"]`).prop('disabled', slot.extra !== null);

    $(`${main} .location-selector .component-search__close-button`).css(
      'display', slot.isMapPick || slot.location !== null ? 'block' : 'none'
    );

    if (slot.isMapPick || slot.location !== null) {
      $(`${main} .location-selector .component-search__validation-error`).css('display', 'none');
    }
    if (slot.extra !== null) {
      $(`${main} .extra-selector .component-search__validation-error`).css('display', 'none');
    }

    const showPort = slot.showAddPort();
    const showArea = slot.showAddArea(store.locations);
    $(`${main} .optional-port`).css('display', showPort ? 'block' : 'none');
    $(`${main} .optional-area`).css('display', showArea ? 'block' : 'none');

    // Toggle class used to offset the close button on destination only when map-picked
    // (map trigger stays visible for map picks; hidden for dropdown picks to avoid overlap).
    $(`${main} .location-selector`).toggleClass('is-map-pick', slot.isMapPick);

    // Hide map trigger when extra is in play OR a dropdown selection is active.
    // For map picks the trigger stays visible (close button is offset via CSS).
    const extraInPlay = showPort || showArea || slot.hasExtra;
    $(`${main} .js-location-map-trigger`).css('display', (extraInPlay || slot.location !== null) ? 'none' : '');
  }

  _syncFieldDOM(wrapperSel, item) {
    const $w = $(wrapperSel);
    $w.find('input[type="search"]').first().val(item?.display_name || '');
    const $hiddenInputs = $w.find('input[type="hidden"]');
    $hiddenInputs.first().val(item?.id || '');
    const $nameHidden = $hiddenInputs.filter('.origin-location-name-hidden, .destination-location-name-hidden, .origin-extra-name-hidden, .destination-extra-name-hidden');
    $nameHidden.val(item ? (item.display_name || item.value || '') : '');
    $w.find('.flag-wrapper').css('display', item ? 'flex' : 'none');
    const $flag = $w.find('.flag-wrapper .flag');
    const $icon = $w.find('.flag-wrapper .icon');
    if (item?.imgUrl) {
      $flag.attr('src', item.imgUrl).css('display', '');
    } else {
      $flag.attr('src', '').css('display', 'none');
    }
    if (item?.iconUrl) {
      $icon.attr('src', item.iconUrl).css('display', '');
    } else {
      $icon.attr('src', '').css('display', 'none');
    }
    $w.find('.marker-wrapper').css('display', item ? 'none' : 'inline-block');
  }

  _syncLabels() {
    const { _slot: slot, _main: main, _t: t } = this;
    const origin = slot.isOrigin;

    const locIsPort = slot.location?.port ?? false;
    const extraIsPort = slot.extra
      ? slot.extra.port
      : (slot.location ? !slot.location.port : false);

    $(`${main} .location-selector label`).html(
      this._labelFor(origin, locIsPort),
    );
    $(`${main} .extra-selector label`).html(
      this._labelFor(origin, extraIsPort),
    );
  }

  _labelFor(isOrigin, isPort) {
    const t = this._t;
    if (isOrigin) return isPort ? t.origin_port : t.origin_area;
    return isPort ? t.destination_port : t.destination_area;
  }

  // ── Validation ─────────────────────────────────────────

  _validateField(target) {
    const { _slot: slot, _main: main, _store: store } = this;
    const isExtra = target === 'extra';
    const inputSel = isExtra ? `${main} .extra-search` : `${main} .location-search`;
    const errorSel = isExtra
      ? `${main} .extra-selector .component-search__validation-error`
      : `${main} .location-selector .component-search__validation-error`;

    const typedValue = $(inputSel).val();
    const $error = $(errorSel);

    if (!typedValue || typedValue === '') {
      $error.css('display', 'none');
      return;
    }

    const pool = slot.filterLocations(store.locations, target);
    const results = searchLocations(pool, typedValue);

    if (!results.length || typedValue !== results[0].display_name) {
      $error.css('display', 'block').text(`${typedValue} is not a valid location`);
    } else {
      $error.css('display', 'none');
    }
  }
}

/**
 * Autocomplete mode handler for {@link LocationPicker}.
 * Implements the mode-handler interface expected by
 * {@link LocationPicker#registerMode}.
 */
export const AutocompleteMode = {
  name: MODES.AUTOCOMPLETE,
  _instances: new Map(),

  attach(slot, store) {
    const translation = initLocale($('body').data('locale'));
    const instance = new SlotAutocomplete(slot, store, translation);
    this._instances.set(slot.role, instance);
    instance.attach();
  },

  detach(slot) {
    const instance = this._instances.get(slot.role);
    if (instance) {
      instance.detach();
      this._instances.delete(slot.role);
    }
  },
};

/**
 * Bind form-level validation that mirrors the original behaviour:
 * prevent Enter-key submission and check that every slot has a
 * selected location before allowing submit.
 */
export function bindFormValidation(picker) {
  $('#compare form').on('keyup keypress', function (e) {
    if (e.which === 13) return false;
  });

  $('#compare form').on('submit', function () {
    for (const role of ['origin', 'destination']) {
      const slot = picker.store.getSlot(role);
      if (!slot) continue;
      const main = MAIN_SEL(role);
      if (!slot.hasLocation) {
        const typed = $(`${main} .location-search`).val();
        const $error = $(`${main} .location-selector .component-search__validation-error`);
        if (!typed || typed === '') {
          $error.css('display', 'block').text('Please select a location');
        } else {
          $error.css('display', 'block').text(`${typed} is not a valid location`);
        }
      }
    }

    const $errors = $(this).find('.component-search__validation-error');
    return $.grep($errors, ve => $(ve).css('display') !== 'none').length === 0;
  });
}
